import type { ChatMessage } from './types';

// The slice of an Amazon Connect ChatSession this app actually uses. Narrowing it
// to an interface is what lets the mock harness and the unit tests stand in for
// the real thing — nothing below this file references window.connect.
export interface ChatSessionHandle {
  connect(): Promise<unknown>;
  sendMessage(args: { contentType: string; message: string }): Promise<unknown> | unknown;
  disconnectParticipant(): Promise<unknown>;
  onMessage(handler: (event: InboundMessageEvent) => void): void;
  onEnded(handler: () => void): void;
  onConnectionBroken(handler: () => void): void;
}

export interface InboundMessageEvent {
  data?: {
    Id?: string;
    Content?: string;
    ContentType?: string;
    DisplayName?: string;
    ParticipantRole?: string;
    // ISO 8601 instant the message was sent, per the Connect transcript item.
    AbsoluteTime?: string;
  };
}

export interface ChatDetails {
  contactId: string;
  participantId: string;
  participantToken: string;
}

export type ChatSessionFactory = (details: ChatDetails) => ChatSessionHandle;

// Default factory: the real ChatJS session, created from the globals the
// 'amazon-connect-chatjs' import installs on window.
export const createConnectChatSession: ChatSessionFactory = (details) => {
  const connect = (window as unknown as { connect?: { ChatSession?: { create(args: unknown): ChatSessionHandle } } }).connect;
  if (!connect?.ChatSession) {
    throw new Error('Chat library not loaded. Please refresh and try again.');
  }

  return connect.ChatSession.create({
    chatDetails: {
      contactId: details.contactId,
      participantId: details.participantId,
      participantToken: details.participantToken,
    },
    options: { region: 'us-east-1' },
    type: 'CUSTOMER',
  });
};

// Translate a ChatJS inbound event into the app's message shape. Returns null for
// events that carry no displayable content — typing indicators and the like come
// through the same handler.
export function toChatMessage(event: InboundMessageEvent, fallbackId: () => string): ChatMessage | null {
  const data = event.data;
  if (!data?.Content) return null;

  const role = data.ParticipantRole;
  // SYSTEM messages are the contact flow speaking (queue position, greetings).
  const kind = role === 'AGENT' ? 'agent' : role === 'CUSTOMER' ? 'customer' : 'system';

  return {
    id: data.Id || fallbackId(),
    kind,
    sender: data.DisplayName || (kind === 'agent' ? 'Agent' : kind === 'customer' ? 'You' : 'System'),
    content: data.Content,
    // Prefer the server's instant; fall back to now only if it is absent.
    at: data.AbsoluteTime || new Date().toISOString(),
  };
}
